import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { albumsApi, eventsApi, getFileUrl } from "../lib/api";
import { ArrowLeft, Camera, FolderOpen, Loader2, Image as ImageIcon, Download, Play, Volume2, VolumeX } from "lucide-react";
import { useRef } from "react";
import { Button } from "../components/ui/button";
import PremiumUpgradeModal from "../components/PremiumUpgradeModal";

export default function AlbumsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumMedia, setAlbumMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [eventInfo, setEventInfo] = useState(null);
  const [blockedOpen, setBlockedOpen] = useState(false);

  useEffect(() => {
    loadAlbums();
  }, [eventId]);

  const loadAlbums = async () => {
    try {
      // Check plan first - albums are premium only
      const { data: ev } = await eventsApi.get(eventId);
      setEventInfo(ev);
      if (ev?.plan === "free") {
        setBlockedOpen(true);
        setLoading(false);
        return;
      }
      const { data } = await albumsApi.list(eventId);
      setAlbums(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openAlbum = async (albumName) => {
    setSelectedAlbum(albumName);
    setLoadingMedia(true);
    try {
      const { data } = await albumsApi.media(eventId, albumName);
      setAlbumMedia(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleDownload = (albumName) => {
    setDownloading(true);
    const url = albumName
      ? albumsApi.downloadAlbum(eventId, albumName)
      : albumsApi.downloadAll(eventId);
    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setDownloading(false), 3000);
  };

  const albumColors = {
    "Chegada": "#22C55E",
    "Churrasco": "#F97316",
    "Festa": "#FF6B6B",
    "Videos": "#8B5CF6",
    "Momentos Especiais": "#EC4899",
    "Selfies": "#3B82F6",
    "Grupo": "#14B8A6",
    "Decoracao": "#EAB308"
  };

  return (
    <div className="min-h-screen bg-[#FFFBF7] pb-20" data-testid="albums-page">
      {/* Header */}
      <header className="sticky top-0 w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917] z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selectedAlbum ? (
              <button onClick={() => setSelectedAlbum(null)} data-testid="back-to-albums">
                <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
              </button>
            ) : (
              <Link to={`/feed/${eventId}`}>
                <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
              </Link>
            )}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#8B5CF6] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917]">
                <FolderOpen className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-black text-lg tracking-tight text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {selectedAlbum || "Albums"}
              </span>
            </div>
          </div>
          <Button
            onClick={() => handleDownload(selectedAlbum)}
            disabled={downloading}
            className="bg-[#8B5CF6] text-white border-2 border-[#1C1917] rounded-full font-bold px-4 py-2 text-sm shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
            data-testid="download-album-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" strokeWidth={2.5} /> Baixar</>}
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#8B5CF6]" />
          </div>
        ) : selectedAlbum ? (
          // Album Media View
          <div>
            {loadingMedia ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#8B5CF6]" />
              </div>
            ) : albumMedia.length === 0 ? (
              <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-12 text-center">
                <ImageIcon className="w-10 h-10 text-[#78716C] mx-auto mb-3" strokeWidth={2.5} />
                <p className="font-bold text-[#1C1917]">Nenhuma foto neste album</p>
                <p className="text-sm text-[#78716C] mt-1">As fotos serao organizadas automaticamente pela IA</p>
              </div>
            ) : (
              <div className="columns-2 sm:columns-3 gap-3">
                {albumMedia.map((item) => {
                  const isVideo = item.type === "video" || item.content_type?.startsWith("video");
                  return (
                    <div key={item.media_id} className="break-inside-avoid mb-3">
                      <div className="bg-white border-2 border-[#1C1917] shadow-[2px_2px_0px_#1C1917] rounded-xl overflow-hidden">
                        {isVideo ? (
                          <div className="relative">
                            <video
                              src={getFileUrl(item.storage_path)}
                              controls
                              playsInline
                              preload="metadata"
                              className="w-full object-cover"
                            />
                            <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">Video</span>
                          </div>
                        ) : (
                          <img
                            src={getFileUrl(item.storage_path)}
                            alt={item.description || ""}
                            className="w-full object-cover"
                            loading="lazy"
                          />
                        )}
                        {item.description && (
                          <p className="px-2 py-1.5 text-xs text-[#78716C]">{item.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          // Albums Grid
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {albums.map((album) => (
              <button
                key={album.album_id}
                onClick={() => openAlbum(album.name)}
                className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-5 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#1C1917] transition-all text-left"
                data-testid={`album-${album.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div
                  className="w-12 h-12 border-2 border-[#1C1917] rounded-lg flex items-center justify-center mb-3 shadow-[2px_2px_0px_#1C1917]"
                  style={{ backgroundColor: albumColors[album.name] || "#8B5CF6" }}
                >
                  <FolderOpen className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-bold text-[#1C1917] text-sm" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {album.name}
                </h3>
                <p className="text-xs text-[#78716C] mt-1">{album.media_count || 0} fotos</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 w-full bg-white border-t-2 border-[#1C1917] z-50" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <div className="max-w-lg mx-auto flex justify-between items-center px-6 py-2">
          <button
            onClick={() => navigate(`/feed/${eventId}`)}
            className="flex flex-col items-center gap-0.5 text-[#78716C]"
          >
            <ImageIcon className="w-6 h-6" strokeWidth={2.5} />
            <span className="text-[10px] font-bold uppercase">Feed</span>
          </button>
          <button
            onClick={() => navigate(`/feed/${eventId}`)}
            className="w-14 h-14 -mt-6 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-full flex items-center justify-center shadow-[3px_3px_0px_#1C1917]"
          >
            <Camera className="w-7 h-7 text-white" strokeWidth={2.5} />
          </button>
          <button className="flex flex-col items-center gap-0.5 text-[#8B5CF6]">
            <FolderOpen className="w-6 h-6" strokeWidth={2.5} />
            <span className="text-[10px] font-bold uppercase">Albums</span>
          </button>
        </div>
      </nav>

      {/* Albums Blocked Modal - shown when event is on free plan */}
      <PremiumUpgradeModal
        eventId={eventId}
        mediaCount={eventInfo?.media_count || 0}
        freeLimit={eventInfo?.free_limit || 30}
        plan={eventInfo?.plan || "free"}
        reason="albums"
        open={blockedOpen}
        onOpenChange={(v) => {
          setBlockedOpen(v);
          // If the host closes the blocker without upgrading, send them back
          // to the event page since they can't use this page on free plan.
          if (!v && eventInfo?.plan === "free") {
            navigate(`/event/${eventId}`);
          }
        }}
      />
    </div>
  );
}
